import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "../ui/Text";
import { Price } from "../ui/Price";
import { getDiscountConfigForSelection } from "../../utils/discountUtils";
import { calculateProductPrice } from "../../utils/inventoryUtils";

interface TieredDiscountDisplayProps {
  product: any;
  selectedVariantOptions: Record<string, string>;
  locationId?: string;
  currentQuantity: number;
  currency: string;
  isPluginEnabled?: (pluginId: string) => boolean;
  onQuantityChange?: (quantity: number) => void;
}

export const TieredDiscountDisplay: React.FC<TieredDiscountDisplayProps> = ({
  product,
  selectedVariantOptions,
  locationId,
  currentQuantity,
  currency,
  isPluginEnabled,
  onQuantityChange,
}) => {
  const { t } = useTranslation();

  // Get discount config
  const discountConfig = useMemo(() => {
    if (!isPluginEnabled?.("discounts-promos") || !locationId) {
      return null;
    }

    const selectedOptions = Object.entries(selectedVariantOptions).map(
      ([optionName, optionValue]) => ({
        optionName,
        optionValue,
      }),
    );

    return getDiscountConfigForSelection(
      product,
      selectedOptions,
      locationId,
    );
  }, [product, selectedVariantOptions, locationId, isPluginEnabled]);

  // Check if tiered discounts are available
  const tieredDiscounts = useMemo(() => {
    if (
      !discountConfig ||
      discountConfig.mode !== "tiered" ||
      !discountConfig.tiers ||
      discountConfig.tiers.length === 0
    ) {
      return null;
    }

    // Sort tiers by minQuantity
    const sortedTiers = [...discountConfig.tiers].sort(
      (a, b) => a.minQuantity - b.minQuantity,
    );

    // Get base price for calculations (without discounts)
    const selectedOptions = Object.entries(selectedVariantOptions).map(
      ([optionName, optionValue]) => ({
        optionName,
        optionValue,
      }),
    );

    const basePrice = calculateProductPrice(
      product,
      selectedOptions,
      locationId,
    );

    // Calculate price for each tier
    const tiersWithPrices = sortedTiers.map((tier) => {
      const tierType = tier.type ?? discountConfig.type;
      const tierValue = tier.value;

      let discountPerUnit = 0;
      if (tierType === "amount") {
        discountPerUnit = Math.min(basePrice, Math.max(0, tierValue));
      } else {
        discountPerUnit = Math.min(
          basePrice,
          Math.max(0, basePrice * (tierValue / 100)),
        );
      }

      const pricePerUnit = Math.max(0, basePrice - discountPerUnit);
      const discountPercentage =
        tierType === "percentage"
          ? tierValue
          : Math.round((discountPerUnit / basePrice) * 100);

      return {
        ...tier,
        minQuantity: tier.minQuantity,
        pricePerUnit: Math.round(pricePerUnit * 100) / 100,
        discountPerUnit: Math.round(discountPerUnit * 100) / 100,
        discountPercentage,
        totalSavings: Math.round(discountPerUnit * tier.minQuantity * 100) / 100,
      };
    });

    return {
      basePrice,
      tiers: tiersWithPrices,
    };
  }, [discountConfig, product, selectedVariantOptions, locationId, isPluginEnabled]);

  // Don't render if no tiered discounts
  if (!tieredDiscounts) {
    return null;
  }

  // Find current tier
  const currentTier = tieredDiscounts.tiers
    .filter((tier) => currentQuantity >= tier.minQuantity)
    .sort((a, b) => b.minQuantity - a.minQuantity)[0];

  // Find next tier (if not already at highest)
  const nextTier = tieredDiscounts.tiers.find(
    (tier) => tier.minQuantity > currentQuantity,
  );

  return (
    <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-start gap-2 mb-3">
        <div className="flex-shrink-0 mt-0.5">
          <svg
            className="w-5 h-5 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <div className="flex-1">
          <Text weight="semibold" className="text-green-800 text-sm mb-1 font-bold">
            {t("productDetail.bulkDiscounts") || "Bulk Discounts Available"}
          </Text>
          <Text size="xs" className="text-green-700">
            {t("productDetail.saveMore") ||
              "Buy more and save! Prices decrease as quantity increases."}
          </Text>
        </div>
      </div>

      <div className="space-y-2">
        {tieredDiscounts.tiers.map((tier, index) => {
          const isActive = currentTier?.minQuantity === tier.minQuantity;
          const isUnlocked = currentQuantity >= tier.minQuantity;
          const isNext = nextTier?.minQuantity === tier.minQuantity;
          const isLowerThanActive =
            currentTier && tier.minQuantity < currentTier.minQuantity;
          const hasNoActiveTier = !currentTier;

          return (
            <div
              key={index}
              className={`relative rounded-md p-3 transition-all ${
                isActive
                  ? "bg-green-100 border-2 border-green-500 shadow-md scale-[1.02]"
                  : isLowerThanActive
                    ? "bg-gray-50 border border-gray-200 opacity-60"
                    : isNext
                      ? "bg-emerald-50 border-2 border-emerald-400 border-dashed"
                      : hasNoActiveTier
                        ? tier.minQuantity >=
                            (tieredDiscounts.tiers[tieredDiscounts.tiers.length - 1]
                              ?.minQuantity ?? 0)
                          ? "bg-green-50 border-2 border-green-300 hover:border-green-400 hover:shadow-sm"
                          : "bg-blue-50 border border-blue-300 hover:border-blue-400 hover:shadow-sm"
                        : "bg-blue-50 border border-blue-300 hover:border-blue-400 hover:shadow-sm"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  {/* Quantity badge */}
                  <div
                    className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm ${
                      isActive
                        ? "bg-green-500 text-white shadow-lg"
                        : isLowerThanActive
                          ? "bg-gray-200 text-gray-500"
                          : isNext
                            ? "bg-emerald-300 text-emerald-900"
                            : hasNoActiveTier
                              ? tier.minQuantity >=
                                  (tieredDiscounts.tiers[tieredDiscounts.tiers.length - 1]
                                    ?.minQuantity ?? 0)
                                ? "bg-green-400 text-green-900"
                                : "bg-blue-200 text-blue-800"
                              : "bg-blue-200 text-blue-800"
                    }`}
                  >
                    {tier.minQuantity}+
                  </div>

                  {/* Price info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <Price
                        amount={tier.pricePerUnit}
                        originalAmount={
                          tier.pricePerUnit < tieredDiscounts.basePrice
                            ? tieredDiscounts.basePrice
                            : undefined
                        }
                        currency={currency}
                        size="sm"
                        className={
                          isActive
                            ? "text-green-700 font-bold"
                            : isLowerThanActive
                              ? "text-gray-500"
                              : isNext
                                ? "text-emerald-700"
                                : hasNoActiveTier
                                  ? tier.minQuantity >=
                                      (tieredDiscounts.tiers[tieredDiscounts.tiers.length - 1]
                                        ?.minQuantity ?? 0)
                                    ? "text-green-700"
                                    : "text-blue-700"
                                  : "text-blue-700"
                        }
                      />
                      <Text
                        size="xs"
                        className={
                          isActive
                            ? "text-green-700"
                            : isLowerThanActive
                              ? "text-gray-500"
                              : isNext
                                ? "text-emerald-600"
                                : hasNoActiveTier
                                  ? tier.minQuantity >=
                                      (tieredDiscounts.tiers[tieredDiscounts.tiers.length - 1]
                                        ?.minQuantity ?? 0)
                                    ? "text-green-600"
                                    : "text-blue-600"
                                  : "text-blue-600"
                        }
                      >
                        {t("productDetail.perUnit") || "per unit"}
                      </Text>
                    </div>
                    {tier.discountPercentage > 0 && (
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                            isActive
                              ? "bg-green-600 text-white"
                              : isLowerThanActive
                                ? "bg-gray-200 text-gray-600"
                                : isNext
                                  ? "bg-emerald-200 text-emerald-800"
                                  : hasNoActiveTier
                                    ? tier.minQuantity >=
                                        (tieredDiscounts.tiers[tieredDiscounts.tiers.length - 1]
                                          ?.minQuantity ?? 0)
                                      ? "bg-green-200 text-green-800"
                                      : "bg-blue-200 text-blue-800"
                                    : !isUnlocked
                                      ? "bg-blue-200 text-blue-800"
                                      : "bg-green-200 text-green-800"
                          }`}
                        >
                          {tier.discountPercentage}%{" "}
                          {t("productDetail.off") || "OFF"}
                        </span>
                        {tier.minQuantity > 1 && (
                          <Text
                            size="xs"
                            className={
                              isActive
                                ? "text-green-700"
                                : isLowerThanActive
                                  ? "text-gray-500"
                                  : isNext
                                    ? "text-emerald-600"
                                    : hasNoActiveTier
                                      ? tier.minQuantity >=
                                          (tieredDiscounts.tiers[tieredDiscounts.tiers.length - 1]
                                            ?.minQuantity ?? 0)
                                        ? "text-green-600"
                                        : "text-blue-600"
                                      : "text-blue-600"
                            }
                          >
                            {t("productDetail.save") || "Save"}{" "}
                            {currency}{" "}
                            {tier.totalSavings.toFixed(2)}{" "}
                            {t("productDetail.total") || "total"}
                          </Text>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action button - hide when active */}
                {onQuantityChange && !isActive && (
                  <button
                    type="button"
                    onClick={() => onQuantityChange(tier.minQuantity)}
                    className={`ml-2 flex-shrink-0 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors shadow-sm ${
                      isLowerThanActive
                        ? "bg-gray-300 hover:bg-gray-400 text-gray-700"
                        : isNext
                          ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                          : hasNoActiveTier
                            ? tier.minQuantity >=
                                (tieredDiscounts.tiers[tieredDiscounts.tiers.length - 1]
                                  ?.minQuantity ?? 0)
                              ? "bg-green-500 hover:bg-green-600 text-white"
                              : "bg-blue-500 hover:bg-blue-600 text-white"
                            : "bg-blue-500 hover:bg-blue-600 text-white"
                    }`}
                  >
                    {t("productDetail.select") || "Select"}
                  </button>
                )}

                {/* Active indicator */}
                {isActive && (
                  <div className="ml-2 flex-shrink-0">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Current savings indicator */}
      {currentTier && currentQuantity >= currentTier.minQuantity && (
        <div className="mt-3 pt-3 border-t border-green-300">
          <div className="flex items-center justify-between">
            <Text size="xs" className="text-green-700 font-semibold">
              {t("productDetail.currentSavings") || "Your current savings"}:
            </Text>
            <Text size="sm" className="text-green-700 font-bold">
              {currency}{" "}
              {(
                currentTier.discountPerUnit * currentQuantity
              ).toFixed(2)}
            </Text>
          </div>
        </div>
      )}
    </div>
  );
};
